import { cn } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';

interface ProgressBarProps {
  readonly current: number;
  readonly total: number;
  readonly showLabel?: boolean;
  readonly showPercentage?: boolean;
  readonly showCount?: boolean;
  readonly size?: 'sm' | 'md' | 'lg';
  readonly variant?: 'default' | 'success' | 'warning' | 'danger';
  readonly className?: string;
}

const variantConfig = {
  default: 'bg-primary',
  success: 'bg-green-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
};

// Helper function to get auto variant based on percentage - extracted to fix nested ternary (S3358)
function getAutoVariant(percentage: number): 'success' | 'default' | 'warning' | 'danger' {
  if (percentage === 100) {
    return 'success';
  }
  if (percentage >= 75) {
    return 'default';
  }
  if (percentage >= 50) {
    return 'warning';
  }
  return 'danger';
}

const sizeConfig = {
  sm: 'h-1.5',
  md: 'h-2',
  lg: 'h-3',
};

export function ProgressBar({
  current,
  total,
  showPercentage = true,
  showCount = false,
  size = 'md',
  variant = 'default',
  className,
}: Readonly<ProgressBarProps>) {
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;

  // Auto-select variant based on percentage
  const autoVariant = getAutoVariant(percentage);

  const selectedVariant = variant === 'default' ? autoVariant : variant;

  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Progress
            value={percentage}
            className={cn('w-full', sizeConfig[size])}
            indicatorClassName={variantConfig[selectedVariant]}
          />
        </div>
        <div className="flex items-center gap-2">
          {showCount && (
            <span className="text-xs font-medium text-muted-foreground">
              {current}/{total}
            </span>
          )}
          {showPercentage && (
            <span className="text-xs font-medium text-muted-foreground min-w-[2.5rem] text-right">
              {percentage}%
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
